import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import CardType from 'Components/CardType';
import GDRowActions from 'Components/GDTable/GDRowActions';
import GDCurrencyCodeCell from 'Components/GDTable/GDCurrencyCodeCell';
import GDColorCodedCell from 'Components/GDTable/GDColorCodedCell';
import GDDateCell from 'Components/GDTable/GDDateCell';
import Badge from 'Components/Badge/Badge.jsx';
import InformationBubble from 'Components/information-bubble/InformationBubble';
import { customFormatclass, customClassCurrency, customClassTabular } from '../GDCustomFormat/GDCustomFormat';
import OpenNewWindowIcon from 'Components/icons/OpenNewWindowIcon/OpenNewWindowIcon.jsx';
import BookMarkIcon from 'Containers/Icon/BookMarkIcon/BookMarkIcon';
import GDAccountNumberCell from '../GDAccountNumberCell/GDAccountNumberCell';
import TrashIcon from 'Containers/Icon/TrashIcon/TrashIcon';
import GDMerchantNumberCell from '../GDMerchantNumberCell/GDMerchantNumberCell';
import GDDueDateCell from '../GDDueDateCell/GDDueDateCell';
import TableCheckbox from 'Components/TableCheckbox/TableCheckbox.jsx';
import InfinityBox from 'Components/InfinityBox/InfinityBox.jsx';
import BVLoader20 from 'Components/loaders/BVLoader/BVLoader20';
import Button from 'Components/Button';
import ImageLightIcon from 'Components/icons/ImageLightIcon';
import DecrementArrowIcon from '../../icons/DecrementArrowIcon';
import IncrementArrowIcon from '../../icons/IncrementArrowIcon';
import ToggleButton from '../../../../dims-common-ui/components/ToggleButton/ToggleButton';

import 'antd/dist/reset.css';

import './GDTableContainer.scss';
import DisabledIncrementArrowIcon from '../../icons/IncrementArrowIcon/DisabledIncrementArrowIcon';
import DisabledDecrementArrowIcon from '../../icons/DecrementArrowIcon/DisabledDecrementArrowIcon';


const GDTableContainer = ({
  actionButtonText,
  columns = [],
  data,
  user,
  extraClass,
  tableName,
  sort,
  handleSort,
  dateFormat,
  timeFormat,
  reportName,
  actions,
  rowActionClick,
  contentWrapRequired,
  loadDataOnScroll,
  isPagable,
  openBoxDropDownStyle,
  isLoading,
  isCheckboxOnRowRequired,
  onCheckboxClick,
  selectedRows,
  identifier,
  dragImg,
  onBookMarked,
  bookMarkedRows,
  isReport,
  onToggleChange,
  updateRulePriority,
  translations,
  disableRulePriorityArrows,
  isMerchatActions,
  isActionOpen,
  isActionClick,
  createMREAccess
}) => {
  const wrapperClass = classnames('gd-table__container', {
    [extraClass]: extraClass,
  });
  const [length, setLength] = useState(0);
  const elRef = useRef(null);
  const headerCells = columns.map((column, index) => {
    let headerClass = 'gd-table__container-cell';
    const key = `${index} ${column.columnKey}`;

    let flag = false;
    let colFlag = false;
    let rowLen = 0;
    const colNameLength = column.name.trim().length;
    const columnLabel = column.columnKey;
    for (const rowData of data) {
      if (rowData[columnLabel] !== null && rowData[columnLabel] !== undefined) {
        const rowLength = (column.displayType === 'CURRENCY' && rowData[columnLabel].value !== null && rowData[columnLabel].value !== undefined) ? rowData[columnLabel].value.length : rowData[columnLabel].length;
        if (column.displayType !== 'DATE' && rowLength > 15) {
          flag = true;
          if (rowLength > rowLen) {
            rowLen = rowLength;
          }
          if (colNameLength > rowLen) {
            colFlag = true;
          }
        }
      }
    }

    if (column.isSortable) {
      headerClass += ' sort';
    }

    if (sort.key === column.columnKey) {
      headerClass += sort.order ? ' sort-desc' : ' sort-asc';
    }

    if (column.displayType === 'CURRENCY') {
      headerClass += ' right-align';
    } else {
      headerClass += ' left-align';
    }
    const headerName = colFlag === true ? `${column.name.substring(0, `${rowLen}`)}...` : column.name;
    return (
      <>
        {
          isCheckboxOnRowRequired && column.description == 'Checkbox' && (
            <th
              key={key}
              id={`${reportName}_${column.columnKey}_column`}
              className={headerClass}
              {...column.isSortable && {
                onClick: () => handleSort(column.columnKey),
              }}
            >
              <InformationBubble
                tooltipContent={(data.length === selectedRows.length ? 'Deselect All' : 'Select All')}
                place={'bottom'}
                id={`${reportName}_${column.columnKey}_column`}
              >
                <span><TableCheckbox
                  id={key}
                  isSvg
                  onChange={() => onCheckboxClick(data.length === selectedRows.length ? [] : data.map(row => { return row[identifier] }))}
                  isChecked={data.length === selectedRows.length && data.length !== 0}
                />
                </span>
              </InformationBubble> 
            </th>
          )
        }
        {
          column.description !== 'Checkbox' && (
            <th
              key={key}
              id={`${reportName}_${column.columnKey}_column`}
              className={headerClass}
              {...column.isSortable && {
                onClick: () => handleSort(column.columnKey),
              }}
            >
              <InformationBubble
                tooltipContent={column.name}
                place={'bottom'}
                id={`${reportName}_${column.columnKey}_column`}
              >
                <span className="gd-header-text" >
                  {(colNameLength > 15) ? `${column.name.substring(0, 15)}...` : headerName}

                </span>
              </InformationBubble>
              {reportName == 'MRE' && column.columnKey == 'priorityOrder' && <InformationBubble
                id={column.columnKey}
                info
                tooltipContent={column.toolTip}
                reportName={reportName}
              />}
            </th>
          )
        }
      </>
    );
  });

  if (actions.length) {
    headerCells.push(
      <th key="rowActions" className="gd-table__container-cell" />,
    );
  }
  const rows = data.map((row, keySeqKey) => {
    const keyRowActions = `rowActions-${keySeqKey}`;
    const cells = columns.map((column, index2) => {
      const key = `${keySeqKey}-${index2}-${column.columnKey}`;
      if (isCheckboxOnRowRequired && column.columnKey === 'checkboxIcon') {
        return (
          <td key={key} className="image-icon checkbox-padding">
            <TableCheckbox
              id={key}
              isSvg
              onChange={() => onCheckboxClick(row[identifier])}
              isChecked={selectedRows.includes(row[identifier])}// {() => onCheckBoxchange(key)}
            />
          </td>
        );
      }
      if (column.displayType === 'ICON') {
        switch (column.columnKey) {
          case 'BookMarkIcon':
            return (

              <td key={key}
                className="image-icon"
              >
                {
                  user && user.parsedPermissions && user.parsedPermissions.case &&
                  user.parsedPermissions.case.canBookmark &&
                  (
                    <BookMarkIcon id={key} isBookMarked={bookMarkedRows !== undefined && bookMarkedRows.includes(row.caseStageId)} toggleBookMark={() => onBookMarked(keySeqKey, row.caseStageId)} height={20} width={20} />
                  )

                }
              </td>

            );
          case 'FolderIcon':
            const boxTriggerClasses = classnames('open-folder', {
            });
            return (
              <td key={key} className="image-icon">
                <div
                  id={`${keySeqKey}_open_folder`}
                  role="button"
                  tabIndex={0}
                  className={boxTriggerClasses}
                  disabled={false}
                  aria-expanded
                  onClick={() => rowActionClick(keySeqKey, 'OPEN_CASE_IN_TAB')}
                >
                  <OpenNewWindowIcon 
                  height={20} width={20} 
                  openBoxClass ={'openBox-iconAlign'}
                  />     
                </div>
              </td>
            );
          case 'imageIcon':
            return (
              <td key={key} className="image-icon">
                <Button
                  action={() => rowActionClick(keySeqKey, 'OPEN_CASE')}
                  category="image-tool toggled"
                  icon={ImageLightIcon}
                  iconDirection="left"
                />
              </td>
            );
          case 'status':
            return (
              <td key={key} className={row.type === "myRules" ? "image-icon" : ""}>
                {createMREAccess ? row.type === "myRules" && <ToggleButton
                  key={key}
                  id={key}
                  offText={row.type === "myRules" ? translations.Off : "Off"}
                  onText={row.type === "myRules" ? translations.On : "On"}
                  isChecked={row.status == 'Active' ? true : false}
                  onChange={onToggleChange}
                  row={row}
                >
                </ToggleButton> : row.status}
              </td>

            );
          case 'priorityOrder':
            return (
              <div className="priority-column" style={reportName == "MRE" ? {marginLeft:"15px"} : {}}>
                <div className='priority-element'>{(row[column.columnKey] == data[0].priorityOrder || disableRulePriorityArrows) ? <DisabledIncrementArrowIcon /> : createMREAccess ? <IncrementArrowIcon onClick={() => handleChangePriority(row[column.columnKey], 'INCREMENT')} /> : <DisabledIncrementArrowIcon />}</div>
                <div className="priority-element"><p style={{ padding: "4px" }}>{row[column.columnKey]}</p></div>
                <div className='priority-element'>{(row[column.columnKey] == data[data.length - 1].priorityOrder || disableRulePriorityArrows) ? <DisabledDecrementArrowIcon /> : createMREAccess ? <DecrementArrowIcon onClick={() => handleChangePriority(row[column.columnKey], 'DECREMENT')} /> : <DisabledDecrementArrowIcon />}</div>
              </div>
            );
          case 'trashIcon':
            if (tableName === "Un-Matched Scheme Images" || tableName.includes("Un-Matched Merchant Images")) {
              return (
                <td key={key} className="image-icon">
                  {
                    user && user.parsedPermissions && user.parsedPermissions.queue &&
                    user.parsedPermissions.queue.archiveImages && (
                      <TrashIcon height="20px" width="20px" />
                    )
                  }
                </td>
              );
            } else {
              return (
                <td key={key} className="image-icon">
                  <TrashIcon height="20px" width="20px" />
                </td>
              );
            }

        }
      } else if (!row[column.columnKey] || row[column.columnKey] === '') {
        // short-circuit for when API doesn't even return data for a column
        return (
          <td key={key} className="gd-table__container-cell">
            --
          </td>
        );
      } else
        if (column.columnKey === 'merchant_number') {
          return (
            <GDColorCodedCell
              key={key}
              content={row[column.columnKey]}
            />
          );
        } else if (column.displayType === 'DATE') {
          return (
            <GDDateCell
              key={key}
              date={row[column.columnKey]}
              dateFormat={dateFormat}
              timeFormat={timeFormat}
            />
          );
        } else if (column.displayType === 'DUEDATE') {
          return (
            <GDDueDateCell
              key={key}
              dueDate={row[column.columnKey]}
              dueDateDescription={row[column.bindKey === undefined ? 'daysRemaining' : column.bindkey]}
              dateFormat={dateFormat}
            />
          );
        } else if (column.displayType === 'INDICATOR') {
          return (
            <td key={key} className={'gd-table__container-cell'}>
              {row[column.columnKey] === '1' ? 'Y' : 'N'}
            </td>
          );
        } else if (column.columnKey === 'card_type' || column.displayType === 'CC_BADGE') {
          return (
            <td key={key} className="gd-table__container-cell">
              <CardType
                abbreviation={row[column.columnKey].toUpperCase()}
                type={row[column.columnKey].toUpperCase()}
              />
            </td>
          );
        } else if (column.displayType === 'CURRENCY' || column.displayType === 'NEGATIVE_NUMBER') {
          const normalizedValue = row[column.columnKey] || '--';
          let normalizedUnits = '';
          let valueClass;

          if (row['disputeCurrencyCode'] !== undefined) {
            normalizedUnits = row['disputeCurrencyCode'] || '';
          }
          else if (row['tranCurrencyCode'] !== undefined) {
            normalizedUnits = row['tranCurrencyCode'] || '';
          }
          else
            normalizedUnits = row['currencyCode'] || '';

          if (Number.isNaN(Number.parseFloat(row[column.columnKey]))) {
            return <GDCurrencyCodeCell
              key={key}
              value={normalizedValue}
              units={normalizedUnits}
              customClass={valueClass}
            />
          }

          if (!Number.isNaN(Number.parseFloat(normalizedValue)) && Number.parseFloat(normalizedValue) < 0) { valueClass = customFormatclass(customClassCurrency, column.displayType); }
          return (
            <GDCurrencyCodeCell
              key={key}
              value={normalizedValue}
              units={normalizedUnits}
              customClass={valueClass}
            />
          );
        } else if (column.displayType === 'BADGE') {
          const normalizedValue = row[column.columnKey];
          return (
            <td key={key} className="gd-table__container-cell">
              <Badge
                badgeType={normalizedValue}
                square={false}
              >
                {normalizedValue}
              </Badge>
            </td>
          );
        } else if (column.displayType === 'ACCOUNTNUMBER') {
          return (
            <td key={key} className="gd-table__container-cell">
              <GDAccountNumberCell
                id={key}
                accountNumber={row[column.columnKey]}
                token={row[column.secondaryColumnKey === undefined ? 'globalToken' : column.secondaryColumnKey]}
              />
            </td>

          );
        } else if (column.displayType === 'MERCHANTNUMBER') {
          const merchantNumber = row[column.bindKey === undefined ? 'merchantNumber' : column.bindKey];
          return (
            //  <td key={key} className="gd-table__container-cell">
            <GDMerchantNumberCell
              key={key}
              merchantName={row[column.columnKey]}
              merchantNumber={merchantNumber == null ? '' : merchantNumber}
            />
            // </td>

          );
        } else if (column.displayType == 'RC') {
          return (<td key={key} className="gd-table__container-cell">
            {row[column.columnKey]}
            <span className="reason-code-tooltip">
              <InformationBubble
                id={key}
                info
                tooltipContent={row.reasonDescription === undefined ? row.codeDescription : row.reasonDescription}
              />
            </span>
          </td>

          );
        } else {
          let valueClass;
          let value;
          if (row.type === "otherRules" && column.columnKey === "status") {
            value = row.status === "Active" ? translations.active : translations.inActive;
          } else {
            value = row[column.columnKey];
          }
          if (!Number.isNaN(Number.parseFloat(row[column.columnKey])) && Number.parseFloat(row[column.columnKey]) < 0) {
            valueClass = customFormatclass(customClassTabular, 'OTHER');
            value *= (-1);
          }
          return (
            <td key={key} className={valueClass || contentWrapRequired ? 'gd-table__container-cell content-wrap' : 'gd-table__container-cell'}
            style={reportName == "MRE" ?{ paddingLeft : "20px"}:""}
            >
              {value}
            </td>
          );
        }
    });

    if (actions.length) {
      cells.push(
        <td key={keyRowActions} className="image-icon">
          <GDRowActions
            actions={actions}
            rowActionClick={rowActionClick}
            key={keyRowActions}
            rowIndex={keySeqKey}
            rowId={row.seq_key}
            triggerText={actionButtonText}
            openBoxDropDownStyle={openBoxDropDownStyle}
            rowLength={length}
            elementRefrence={elRef.current}
            setScroll={setScroll}
            reportName={reportName}
            isMerchatActions={isMerchatActions}
            isActionClick = {isActionClick}
          /></td>,
      );
    }

    const isSelectedRow = selectedRows.includes(row[identifier]);

    const floatingPanel = document.getElementById('floating-panel')
    const queueLandingView = document.getElementById('queueManagementLanding');

    function onDrag(e) {
      queueLandingView.style.opacity = `0.5`;
      floatingPanel.style.display = `flex`;
      floatingPanel.style.top = `${e.pageY}px`;
      floatingPanel.style.left = `${e.pageX}px`;
    }

    function onDragStart(e) {
      e.dataTransfer.setData('text/plain', '');
      dragImg = new Image(0, 0);
      dragImg.src =
        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
      e.dataTransfer.setDragImage(dragImg, 0, 0);
      floatingPanel.style.display = `flex`;
    }
    function onDragEnd() {
      queueLandingView.style.opacity = `1`;
      floatingPanel.style.display = `none`;
      floatingPanel.style.top = `0px`;
      floatingPanel.style.left = `0px`;
    }

    return (
      <tr
        key={keySeqKey}
        onDragStart={isSelectedRow ? (e) => onDragStart(e, selectedRows) : () => { }}
        onDrag={isSelectedRow ? (e) => onDrag(e) : () => { }}
        onDragEnd={isSelectedRow ? (e) => onDragEnd(e, selectedRows) : () => { }}
        className={isSelectedRow ? 'gd-table__container-selected-row' : 'gd-table__container-row'}
        draggable={isSelectedRow ? true : false}
      >
        {cells}
      </tr>
    );
  });

  function setScroll(numberOfchildren) {
    if (reportName == "MRE") {
      if (isActionOpen == false) {
        elRef.current.style.paddingBottom = (45 * numberOfchildren) + "px";
      }
    } else if (elRef.current !== null) {
      elRef.current.style.paddingBottom = (45 * numberOfchildren) + "px";
    }
  }

  useEffect(() => {
    if (document !== undefined) {
      let lastRow = document.getElementsByTagName("tbody")[0].getElementsByTagName("tr").length;
      setLength(lastRow);
    }

  });
  function handleChangePriority(priority, action) {
    updateRulePriority(priority, action)

  }

  return (
    <div className={wrapperClass}>

      {isPagable ?
        <div className="gd-table-pagable" ref={elRef} style={{ border: ' 1px solid lightgray', borderTop: '0px solid lightgray', height: isReport ? '400px' : '' }} >
          <table className="gd-table__container">
            <thead className="gd-table__container-header">
              <tr className="gd-table__container-row">{headerCells}</tr>
            </thead>
            <tbody
            >{rows}</tbody>
          </table>
        </div>
        :
        <InfinityBox
          dataLength={rows.length}
          next={loadDataOnScroll}
          hasMore={rows.length < 5 ? false : true}
          height={285}
          minHeight = {reportName == 'MRE' ? "200px" : ""}
          scrollThreshold={0.8}
          loader={isLoading ? <BVLoader20 /> : ''}
        >
          <div className="gd-table-pagable-infinity" ref={elRef} style={{}} >
            <table className="gd-table__container">
              <thead >
                <tr className="gd-table__container-header">{headerCells}</tr>
              </thead>
              <tbody
              >{rows}</tbody>
            </table>
          </div>

        </InfinityBox>
      }

    </div>

  );
};

GDTableContainer.propTypes = {
  actionButtonText: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  dateFormat: PropTypes.string.isRequired,
  extraClass: PropTypes.string,
  handleSort: PropTypes.func,
  rowActions: PropTypes.array,
  timeFormat: PropTypes.string,
  sort: PropTypes.object,
  reportName: PropTypes.string.isRequired,
  actions: PropTypes.array,
  rowActionClick: PropTypes.func,
  selectedRows: PropTypes.array,
  identifier: PropTypes.string,
  onBookMarked: PropTypes.func,
  bookMarkedRows: PropTypes.array,
  onToggleChange: PropTypes.func,
  updateRulePriority: PropTypes.func,
  translations: PropTypes.object,
  disableRulePriorityArrows: PropTypes.bool,
  isMerchatActions: PropTypes.bool,
  isActionOpen: PropTypes.bool,
  isActionClick: PropTypes.bool,
  createMREAccess: PropTypes.bool,
};

GDTableContainer.defaultProps = {
  extraClass: '',
  sort: {},
  handleSort: () => { },
  rowActions: [],
  actions: [],
  timeFormat: 'hh:mm a',
  isLoading: true,
  identifier: '',
  user: '',
  tableName: '',
  onToggleChange: () => { },
  updateRulePriority: () => { },
  translations: {
    active: '',
    inActive: '',
    On: '',
    Off: '',
  },
  disableRulePriorityArrows: false,
  createMREAccess: false,
};

export default GDTableContainer;
